import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import type { CrmRequiredActionItem } from '../lib/crm/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

interface AgentCallbackRow {
  id: string;
  customer_id: string | null;
  opportunity_id: number | null;
  from_number: string;
  name: string | null;
  reason: string | null;
  created_at: string;
  customer_name?: string;
}

const callbacksCache = createBusinessCache<AgentCallbackRow[]>({
  persistKey: 'agent-callbacks',
});

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const last10 = digits.slice(-10);
  if (last10.length === 10) {
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  }
  return raw;
}

export function buildAgentCallbackRequiredActions(
  rows: AgentCallbackRow[],
): CrmRequiredActionItem[] {
  return rows.map((row) => {
    const created = new Date(row.created_at);
    const displayName =
      row.name?.trim() || row.customer_name?.trim() || formatPhone(row.from_number);
    const reason = row.reason?.trim();
    return {
      kind: 'agent_callback' as const,
      callbackId: row.id,
      opportunityId: row.opportunity_id ?? undefined,
      title: displayName,
      customerName: formatPhone(row.from_number),
      subtitle: `${reason || 'Call back requested'} · ${formatDistanceToNow(created, { addSuffix: true })}`,
      sortOrder: -created.getTime(),
      createdAt: row.created_at,
    };
  });
}

/** Pending AI receptionist callbacks — someone is waiting on a return call. */
export function useAgentCallbackRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [callbacks, setCallbacks] = useState<AgentCallbackRow[]>(
    () => callbacksCache.get(businessId) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !callbacksCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchCallbacks = useCallback(async () => {
    if (!businessId || !supabase) return;

    const { data, error: fetchError } = await supabase
      .from('agent_callbacks')
      .select('id, customer_id, opportunity_id, from_number, name, reason, created_at')
      .eq('business_id', businessId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(100);

    if (fetchError) throw fetchError;

    const rows = (data ?? []) as AgentCallbackRow[];
    const customerIds = [...new Set(rows.map((r) => r.customer_id).filter(Boolean))] as string[];
    if (customerIds.length) {
      const { data: customersData } = await supabase
        .from('customers')
        .select('id, first_name, last_name')
        .in('id', customerIds);
      const nameById = new Map<string, string>();
      (customersData ?? []).forEach((c) => {
        const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
        if (name) nameById.set(c.id, name);
      });
      for (const row of rows) {
        if (row.customer_id) row.customer_name = nameById.get(row.customer_id);
      }
    }

    setCallbacks(rows);
    callbacksCache.set(businessId, rows);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setCallbacks([]);
      setIsLoading(false);
      return;
    }

    if (callbacksCache.has(businessId)) {
      setCallbacks(callbacksCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchCallbacks();
    } catch (err) {
      console.error('Error loading agent callback required actions:', err);
      setError('Failed to load callback actions.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchCallbacks]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setCallbacks([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const channel = supabase
      .channel(`agent-callback-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_callbacks',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchCallbacks();
          }, 400);
        }
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchCallbacks]);

  const callbackActions = useMemo(
    () => buildAgentCallbackRequiredActions(callbacks),
    [callbacks],
  );

  return {
    callbacks,
    callbackActions,
    isLoading,
    error,
    refresh: load,
  };
}
